import { useEffect, useRef, useState } from 'react';
import {
  Grid2,
  Button,
  Stack,
  Typography,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Divider,
  Chip
} from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import RequiredIndicator from 'components/@extended/RequiredIndicator';
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import Snackbar from 'utils/Snackbar';

import { createPerformanceCriterion, updatePerformanceCriterion, createNewPCVersion } from '../../services/performanceCriteria.service';

const STORAGE_KEY = 'pc_draft';

interface PCFormValues {
  pcName: string;
  pcCode: string;
  description: string;
  version: string;
  isQuestion: boolean;
  isTask: boolean;
  isViva: boolean;
  questionMarks: number | '';
  taskMarks: number | '';
  vivaMarks: number | '';
  totalMarks: number;
  minMarks: number | '';
  isCritical: boolean;
}

const AddPerformanceCriteria = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useAuth();

  const isEdit = location.state?.isEdit || false;
  const editData = location.state?.data || null;
  const createNewVersionFrom = location.state?.createNewVersionFrom || null;
  const isNewVersionMode = Boolean(createNewVersionFrom);

  const isMounted = useRef(false);

  // ================= INITIAL VALUES =================
  const getInitialValues = (): PCFormValues => {
    const source = isEdit ? editData : createNewVersionFrom;
    if (source) {
      const typeArr: { title: string; marks: number }[] = source?.type || [];
      const findMarks = (title: string) => typeArr.find((t) => t.title === title)?.marks;

      const questionMarks = findMarks('question');
      const taskMarks = findMarks('task');
      const vivaMarks = findMarks('viva');

      return {
        pcName: source?.pcName || '',
        pcCode: source?.pcCode || '',
        description: source?.description || '',
        version: source?.version ?? '1',
        isQuestion: questionMarks !== undefined,
        isTask: taskMarks !== undefined,
        isViva: vivaMarks !== undefined,
        questionMarks: questionMarks ?? '',
        taskMarks: taskMarks ?? '',
        vivaMarks: vivaMarks ?? '',
        totalMarks: source?.totalMarks || 0,
        minMarks: source?.minMarks ?? '',
        isCritical: source?.isCritical ?? false
      };
    }

    return {
      pcName: '',
      pcCode: '',
      description: '',
      version: '1',
      isQuestion: false,
      isTask: false,
      isViva: false,
      questionMarks: '',
      taskMarks: '',
      vivaMarks: '',
      totalMarks: 0,
      minMarks: '',
      isCritical: false
    };
  };

  // ================= VALIDATION =================
  const validationSchema = Yup.object({
    pcName: Yup.string().required('PC Name is required'),
    pcCode: Yup.string().required('PC Code is required'),
    description: Yup.string().required('Performance Statement is required'),
    version: Yup.string().optional(),

    isQuestion: Yup.boolean(),
    isTask: Yup.boolean(),
    isViva: Yup.boolean(),

    questionMarks: Yup.number()
      .min(0)
      .when('isQuestion', {
        is: true,
        then: (s) => s.required('Required')
      }),
    taskMarks: Yup.number()
      .min(0)
      .when('isTask', {
        is: true,
        then: (s) => s.required('Required')
      }),
    vivaMarks: Yup.number()
      .min(0)
      .when('isViva', {
        is: true,
        then: (s) => s.required('Required')
      }),

    totalMarks: Yup.number().min(1, 'Select at least one assessment type'),

    minMarks: Yup.number().when('isCritical', {
      is: true,
      then: (s) => s.required('Min marks required').max(Yup.ref('totalMarks'), 'Cannot exceed total marks')
    })
  });

  // ================= SUBMIT =================
  const handleSubmit = async (values: PCFormValues, helpers: FormikHelpers<PCFormValues>) => {
    helpers.setSubmitting(true);

    const payload = {
      pcName: values.pcName.trim(),
      pcCode: values.pcCode.trim(),
      description: values.description.trim(),
      questionMarks: values.isQuestion ? Number(values.questionMarks) : 0,
      taskMarks: values.isTask ? Number(values.taskMarks) : 0,
      vivaMarks: values.isViva ? Number(values.vivaMarks) : 0,
      totalMarks: values.totalMarks,
      minMarks: values.isCritical ? Number(values.minMarks) : 0,
      isCritical: values.isCritical
    };

    try {
      if (isNewVersionMode && createNewVersionFrom?._id) {
        await createNewPCVersion(createNewVersionFrom._id, payload);
        Snackbar('New Performance Criteria version created', 'success');
      } else if (isEdit) {
        await updatePerformanceCriterion(editData._id, payload);
        Snackbar('Performance Criterion updated', 'success');
      } else {
        const createPayload = { ...payload, version: values.version?.trim() || '1' };
        await createPerformanceCriterion(createPayload);
        Snackbar('Performance Criterion created', 'success');
      }
      localStorage.removeItem(STORAGE_KEY);
      navigate(-1);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to save';
      Snackbar(msg, 'error');
      if (isEdit && msg?.includes('in use')) {
        Snackbar('Use "Create new version" from the list instead of editing.', 'info');
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <MainCard
          content={false}
          title={
            <Stack direction="row" spacing={2} alignItems="center">
              <BackToggle show />
              <Typography variant="h5">
                {isNewVersionMode
                  ? 'Create new Performance Criteria version'
                  : isEdit
                  ? 'Edit Performance Criteria'
                  : 'Add Performance Criteria'}
              </Typography>
            </Stack>
          }
        >
          <Formik initialValues={getInitialValues()} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => {
              // AUTO TOTAL
              useEffect(() => {
                const total =
                  (values.isQuestion ? Number(values.questionMarks || 0) : 0) +
                  (values.isTask ? Number(values.taskMarks || 0) : 0) +
                  (values.isViva ? Number(values.vivaMarks || 0) : 0);

                setFieldValue('totalMarks', total, false);
              }, [values]);

              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Box p={3}>
                    <Grid2 container spacing={2.5}>
                      {/* PC Name */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          PC Name <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput fullWidth name="pcName" value={values.pcName} onChange={handleChange} />
                      </Grid2>

                      {/* PC Code */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>
                          PC Code <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput fullWidth name="pcCode" value={values.pcCode} onChange={handleChange} disabled={isNewVersionMode} />
                      </Grid2>

                      {/* Version - only for create; edit/new-version show read-only */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Version</InputLabel>
                        <OutlinedInput
                          fullWidth
                          name="version"
                          value={values.version}
                          onChange={handleChange}
                          disabled={isEdit || isNewVersionMode}
                          placeholder={isNewVersionMode ? 'Auto-assigned' : 'e.g. 1'}
                        />
                      </Grid2>

                      {/* Description */}
                      <Grid2 size={12}>
                        <InputLabel>
                          PC Description <RequiredIndicator />
                        </InputLabel>
                        <OutlinedInput fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} />
                      </Grid2>

                      <Grid2 size={12}>
                        <Divider />
                      </Grid2>

                      {/* ASSESSMENT COMPONENTS */}
                      <Grid2 size={12}>
                        <Box
                          sx={{
                            borderRadius: 1.5,
                            p: 2,
                            bgcolor: 'grey.50',
                            border: (theme) => `1px dashed ${theme.palette.divider}`
                          }}
                        >
                          <Stack spacing={2}>
                            {/* Type selector */}
                            <Box>
                              <InputLabel sx={{ mb: 0.5 }}>
                                Assessment Types <RequiredIndicator />
                              </InputLabel>
                              {(() => {
                                const typeConfig = [
                                  { key: 'question', label: 'Question', flag: 'isQuestion' },
                                  { key: 'task', label: 'Task', flag: 'isTask' },
                                  { key: 'viva', label: 'Viva', flag: 'isViva' }
                                ] as const;

                                const selectedTypes = typeConfig.filter((t) => (values as any)[t.flag]).map((t) => t.key);

                                const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
                                  const value = event.target.value as string[];

                                  typeConfig.forEach((t) => {
                                    const isSelected = value.includes(t.key);
                                    setFieldValue(t.flag, isSelected);
                                    if (!isSelected) {
                                      const marksField =
                                        t.key === 'question' ? 'questionMarks' : t.key === 'task' ? 'taskMarks' : 'vivaMarks';
                                      setFieldValue(marksField, '');
                                    }
                                  });
                                };

                                return (
                                  <Select
                                    multiple
                                    fullWidth
                                    value={selectedTypes}
                                    onChange={handleTypeChange}
                                    displayEmpty
                                    renderValue={(selected) => {
                                      if ((selected as string[]).length === 0) {
                                        return <Typography color="text.secondary">Select assessment types</Typography>;
                                      }
                                      return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {(selected as string[]).map((value) => {
                                            const item = typeConfig.find((t) => t.key === value);
                                            return <Chip key={value} label={item?.label ?? value} size="small" />;
                                          })}
                                        </Box>
                                      );
                                    }}
                                  >
                                    <MenuItem disabled value="">
                                      <Typography color="text.secondary">Select assessment types</Typography>
                                    </MenuItem>
                                    {typeConfig.map((t) => (
                                      <MenuItem key={t.key} value={t.key}>
                                        {t.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                );
                              })()}
                            </Box>

                            {/* Marks per selected type */}
                            <Grid2 container spacing={2.5}>
                              {[
                                ['isQuestion', 'questionMarks', 'Question Marks'],
                                ['isTask', 'taskMarks', 'Task Marks'],
                                ['isViva', 'vivaMarks', 'Viva Marks']
                              ].map(([flag, marks, label]) =>
                                (values as any)[flag] ? (
                                  <Grid2 size={{ xs: 12, md: 6 }} key={flag}>
                                    <InputLabel sx={{ mb: 0.5 }}>{label}</InputLabel>
                                    <OutlinedInput
                                      type="number"
                                      placeholder="Marks"
                                      value={(values as any)[marks]}
                                      onChange={(e) => setFieldValue(marks, e.target.value)}
                                    />
                                    <FormHelperText error>{(touched as any)[marks] && (errors as any)[marks]}</FormHelperText>
                                  </Grid2>
                                ) : null
                              )}
                            </Grid2>
                          </Stack>
                        </Box>
                      </Grid2>

                      {/* TOTAL */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Total Marks</InputLabel>
                        <OutlinedInput fullWidth value={values.totalMarks} disabled />
                      </Grid2>

                      {/* CRITICAL */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <InputLabel>Min Marks</InputLabel>
                        <OutlinedInput
                          fullWidth
                          type="number"
                          placeholder="Min Marks"
                          disabled={!values.isCritical}
                          value={values.minMarks}
                          onChange={(e) => setFieldValue('minMarks', e.target.value)}
                        />
                        <FormHelperText error>{touched.minMarks && errors.minMarks}</FormHelperText>
                      </Grid2>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={<Switch checked={values.isCritical} onChange={(e) => setFieldValue('isCritical', e.target.checked)} />}
                        label="Critical"
                      />
                    </Grid2>

                    <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
                      <Button variant="outlined" onClick={() => navigate(-1)}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {isNewVersionMode ? 'Create new version' : isEdit ? 'Update Performance Criterion' : 'Create Performance Criterion'}
                      </Button>
                    </Stack>
                  </Box>
                </form>
              );
            }}
          </Formik>
        </MainCard>
      </Grid2>
    </Grid2>
  );
};

export default AddPerformanceCriteria;
